import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import MuiAppBar from '@mui/material/AppBar';
import stringAvatar from '../../lib/stringAvatar';
import LogoutIcon from '@mui/icons-material/Logout';
import { Avatar, Badge, Box, Divider, Fab, IconButton, Menu, MenuItem, styled, Toolbar, Typography } from '@mui/material'
import Assets from '../../Component/Common/Assets';
import Notifications from '../../Component/Notifications/Notifications';
import CustomDrawer from '../../Component/Common/CustomDrawer';
import GlobalSearch from './GlobalSearch';
import localStoreUtil from '../../ApiSetUp/localstore.util';
import { useDispatch } from 'react-redux';
import usePageLoader from '../../Redux/hooks/usePageLoader';
import useSnackBar from '../../Redux/hooks/useSnackBar';
import ErrorHandler from '../../lib/errorHandler';
import { get_all_notifications } from '../../Redux/Actions/AuthUser';
import { HEADER_HEIGHT, SUBNAV_HEIGHT, TERTIARY_HEIGHT, pathForItem } from '../nav';
import { drawerWidth, miniDrawerWidth } from '../Sidebar';

export { HEADER_HEIGHT } from '../nav';

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
    backgroundColor: theme.palette.bgWhite.main,
    boxShadow: "none",
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    borderBottom: "1px solid #0404041A",
    variants: [
        {
            props: ({ open }) => open,
            style: {
                width: `calc(100% - ${drawerWidth}px)`,
                marginLeft: `${drawerWidth}px`,
                transition: theme.transitions.create(['margin', 'width'], {
                    easing: theme.transitions.easing.easeOut,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            },
        },
    ],
}));

const barBtn = (active, dense) => ({
    appearance: 'none',
    border: 0,
    background: 'none',
    cursor: 'pointer',
    flexShrink: 0,
    fontFamily: active ? 'Satoshi-Bold, sans-serif' : 'Satoshi-Medium, sans-serif',
    fontSize: dense ? 12 : 13,
    fontWeight: active ? 700 : 500,
    letterSpacing: dense ? '0.01em' : '0.04em',
    textTransform: dense ? 'none' : 'uppercase',
    color: active ? '#E77843' : '#5D6679',
    padding: dense ? '8px 12px' : '10px 14px',
    borderBottom: active ? '2px solid #E77843' : '2px solid transparent',
    '&:hover': { color: '#E77843', background: '#FFF8F4' },
    '&:focus-visible': { outline: '2px solid #E77843', outlineOffset: -2 },
});

const SubNav = ({ items, active, onSelect, dense, label, padLeft }) => (
    <Box
        role="navigation"
        aria-label={label}
        sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 0.25,
            minHeight: dense ? TERTIARY_HEIGHT : SUBNAV_HEIGHT,
            px: padLeft,
            overflowX: 'auto',
            borderTop: '1px solid rgba(19,19,19,0.08)',
            background: dense ? '#FAFAFA' : '#FFFFFF',
            '&::-webkit-scrollbar': { height: 0 },
        }}
    >
        {items.map((item) => {
            const isActive = active?.path === item.path && active?.name === item.name;
            return (
                <Box
                    key={`${item.path}-${item.name}`}
                    component="button"
                    type="button"
                    onClick={() => onSelect(item)}
                    sx={barBtn(isActive, dense)}
                >
                    {item.name}
                </Box>
            );
        })}
    </Box>
);

const Header = ({ open, logOut, filteredTabs, nav }) => {
    // =============== Hooks ===============
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const setFullPageLoader = usePageLoader();
    const { setSnackBar } = useSnackBar();

    const loggedInUserDetails = localStoreUtil?.get_data("User_Data");
    const userData = loggedInUserDetails ? JSON.parse(loggedInUserDetails) : null;
    const padLeft = open ? '20px' : `${miniDrawerWidth + 16}px`;
    const goItem = (item) => {
        const next = pathForItem(item);
        if (next) navigate(next);
    };

    // =============== States ===============
    const [anchorEl, setAnchorEl] = useState(null);
    const [isOpenNotification, setIsOpenNotification] = useState(false);
    const [notificationData, setNotificationData] = useState({});

    // =============== Handler ===============
    const openMenu = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };


    // ============= get Inventories API =============
    const _get_all_notifications = async (p) => {
        setFullPageLoader(true);
        let response;
        let body = {
            "limit": 10,
            "page": 1,
            "date": null,
        }
        response = await dispatch(get_all_notifications(body));
        let error = await ErrorHandler(response, setSnackBar);
        if (error) {
            if (response?.payload?.data) {
                setNotificationData(response?.payload?.data)
            }
            setFullPageLoader(false);
        }
        setFullPageLoader(false);
    };

    useEffect(() => {
        _get_all_notifications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenNotification])

    return (
        <AppBar position="fixed" open={open}>
            <Toolbar sx={{ display: "flex", alignItems: "center", gap: 2, minHeight: `${HEADER_HEIGHT}px !important`, "&.MuiToolbar-root": { paddingLeft: padLeft, paddingRight: "16px" } }}>
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
                    <GlobalSearch filteredTabs={filteredTabs} />
                </Box>
                <Box display={'flex'} alignItems={'center'} gap={1} flexShrink={0}>
                    <Badge badgeContent={notificationData?.count || 0} sx={{ "& .MuiBadge-badge": { right: "2px", top: "6px", zIndex: 9999, color: "#FFF", fontSize: 10, height: 16, minWidth: 16 } }} color="primary">
                        <Fab onClick={() => setIsOpenNotification(true)} aria-label="Notifications" sx={{ boxShadow: "none", backgroundColor: "#F5F9FB", height: "36px", width: "36px", minHeight: "36px" }}>
                            <Assets absolutePath={true} src={`/assets/img/Notification.svg`} height={18} width={18} />
                        </Fab>
                    </Badge>
                    <IconButton
                        onClick={handleClick}
                        aria-label="Account menu"
                        aria-haspopup="true"
                        aria-expanded={openMenu ? "true" : undefined}
                        sx={{ p: 0 }}
                    >
                        {userData?.profilePicture ? (
                            <Avatar src={userData?.profilePicture} style={{ height: "36px", width: "36px" }} />
                        ) : (
                            userData?.name ? (
                                <Avatar {...stringAvatar(userData?.name)} style={{ height: "36px", width: "36px" }} />
                            ) : (
                                <Avatar style={{ height: "36px", width: "36px" }} />
                            )
                        )}
                    </IconButton>
                </Box>
            </Toolbar>
            {nav?.secondary?.length ? (
                <SubNav
                    items={nav.secondary}
                    active={nav.activeSecondary}
                    onSelect={goItem}
                    label="Section"
                    padLeft={padLeft}
                />
            ) : null}
            {nav?.tertiary?.length ? (
                <SubNav
                    items={nav.tertiary}
                    active={nav.activeTertiary}
                    onSelect={goItem}
                    dense
                    label="Subsection"
                    padLeft={padLeft}
                />
            ) : null}
            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={openMenu}
                onClose={handleClose}
                onClick={handleClose}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            minWidth: "200px",
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: 1.5,
                            "& .MuiMenu-list": {
                                padding: "0px"
                            },
                            '&::before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box p={2} display={"flex"} gap={1} alignItems={"start"}>
                    {userData?.profilePicture ? (
                        <Avatar src={userData?.profilePicture} style={{ height: "50px", width: "50px" }} />
                    ) : (
                        userData?.name ? (
                            <Avatar {...stringAvatar(userData?.name)} style={{ height: "50px", width: "50px" }} />
                        ) : (
                            <Avatar style={{ height: "50px", width: "50px" }} />
                        )
                    )}
                    <Box >
                        <Typography fontSize={"18px"} fontWeight={700}>{userData?.name || "User"}</Typography>
                        <Typography fontSize={"12px"}>{userData?.email || ""}</Typography>
                    </Box>
                </Box>
                <Divider />
                <MenuItem onClick={logOut} sx={{ display: "flex", gap: 1, py: 1, px: 2, bgcolor: "#0000000a" }}>
                    <LogoutIcon fontSize="small" />
                    <Typography fontSize="16px">Logout</Typography>
                </MenuItem>

            </Menu>
            <CustomDrawer
                isDrawerOpen={isOpenNotification}
                handleClose={() => setIsOpenNotification(false)}
                title={`Notification`}
            >
                <Notifications notificationData={notificationData} setIsOpenNotification={setIsOpenNotification} />
            </CustomDrawer>
        </AppBar>
    )
}

export default Header